import { FunnelIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';

import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import {
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@/components/core/Table';
import { useTranslation } from '@/hooks/useTranslation';

import { useOrganizationUsers } from '../user-groups.queries';
import { GroupMember, memberLabel } from '../user-groups.types';

interface SelectableUser extends GroupMember {
    fullName: string;
    checked: boolean;
}

interface AddUsersPanelProps {
    open: boolean;
    members: GroupMember[];
    onAdd: (users: GroupMember[]) => void;
    onRemove: (user: GroupMember) => void;
}

const AddUsersPanel = ({
    open,
    members,
    onAdd,
    onRemove,
}: AddUsersPanelProps) => {
    const { t } = useTranslation();
    const { data, isLoading } = useOrganizationUsers({ enabled: open });
    const [rows, setRows] = useState<SelectableUser[]>([]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (open) {
            setSearch('');
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const existingIds = new Set(members.map((member) => String(member.id)));
        const nextRows = (data ?? [])
            .filter((user) => user.active)
            .map((user) => {
                const alreadyAdded = existingIds.has(String(user.id));
                const fullName = user.fullName || user.userId || '';
                return {
                    ...user,
                    fullName,
                    checked: alreadyAdded,
                };
            })
            .sort((left, right) => left.fullName.toLowerCase().localeCompare(right.fullName.toLowerCase()));
        setRows(nextRows);
    }, [open, data, members]);

    const visibleRows = useMemo(() => {
        const query = search.toLowerCase();
        if (!query) {
            return rows;
        }
        return rows.filter((row) => row.fullName.toLowerCase().includes(query));
    }, [rows, search]);

    if (!open) {
        return null;
    }

    return (
        <div className="tw-my-4 tw-rounded-md tw-border tw-border-solid tw-border-[#dee6ed] tw-p-4">
            <Input
                id="groupUserFilter"
                name="groupUserFilter"
                icon={FunnelIcon}
                value={search}
                placeholder={`${t('SEARCH')} ${t('USER')}..`}
                onChange={(event) => setSearch(event.target.value)}
            />
            <div className="tw-mt-4 tw-max-h-80 tw-overflow-auto tw-border border-table">
                <Table
                    id="select-group-user-table"
                    className="!tw-table tw-w-full !tw-max-h-none !tw-overflow-visible"
                >
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={2} className="tw-text-center">
                                    <Loader />
                                </TableCell>
                            </TableRow>
                        ) : null}
                        {!isLoading && rows.length < 1 ? (
                            <TableRow>
                                <TableCell colSpan={2} className="tw-text-center tw-text-t-sm lg:tw-text-t-base tw-text-t-secondary">
                                    <span>{t('USER_MANAGEMENT.NO_USERS_AVAILABLE')}</span>
                                </TableCell>
                            </TableRow>
                        ) : null}
                        {!isLoading && rows.length > 0 ? visibleRows.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell className="tw-w-12">
                                    <div className="tw-flex tw-h-9 tw-items-center tw-justify-center">
                                        <input
                                            className="check-member tw-h-4 tw-w-4 lg:tw-h-5 lg:tw-w-5"
                                            type="checkbox"
                                            checked={user.checked}
                                            onChange={(event) => {
                                                const checked = event.target.checked;
                                                setRows((current) => current.map((row) => (
                                                    row.id === user.id ? { ...row, checked } : row
                                                )));
                                                if (checked) {
                                                    onAdd([user]);
                                                } else {
                                                    onRemove(user);
                                                }
                                            }}
                                        />
                                    </div>
                                </TableCell>
                                <TableCell className="!tw-pl-4 tw-text-t-sm lg:tw-text-t-base tw-text-t-primary">
                                    <span>{memberLabel(user)}</span>
                                </TableCell>
                            </TableRow>
                        )) : null}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

export { AddUsersPanel };
